'use client';
import { countryCode } from '@payments-checkout/shared/helpers/constants/country-code.const';
import { DOCUMENT_TYPE } from '@payments-checkout/shared/helpers/constants/document-person-type.const';
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { documentType } from '../../types/customer.types';
import { FormContextProps } from './interface';

const FormContext = createContext<FormContextProps | undefined>(undefined);

export const FormProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const intl = useIntl();
  const documentArray = useCallback(() => {
    const documents = [
      DOCUMENT_TYPE.CPF,
      DOCUMENT_TYPE.CNPJ,
      intl.formatMessage({ id: 'customerForm.passport' }),
    ];
    return documents;
  }, []);

  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [name, setName] = useState<string>('');

  const [documentTypes, setDocumentTypes] = useState(documentArray());
  const [documentType, setDocumentType] = useState(
    DOCUMENT_TYPE.CPF as documentType
  );
  const [selectedCountryCode, setSelectedCountryCode] = useState(
    countryCode[0].value
  );
  const [errors, setErrors] = useState<{
    email?: string;
    name?: string;
    phone?: string;
  }>({});
  const [allErrors, setAllErrors] = useState<string[]>([]);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [documentValue, setDocumentValue] = useState('');
  const [documentErrors, setDocumentErrors] = useState<{
    cpf?: string;
    cnpj?: string;
    passport?: string;
  }>({});

  const emailRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);

  const onSelectDocument = useCallback(
    (option: documentType) => {
      if (option === intl.formatMessage({ id: 'customerForm.passport' })) {
        setDocumentType('passport');
      } else {
        setDocumentType(option);
      }
    },
    [intl]
  );

  const validateFields = useCallback(() => {
    const currentErrors: { email?: string; name?: string; phone?: string } = {};

    setErrors(currentErrors);

    const newAllErrors = [
      currentErrors.email,
      currentErrors.name,
      currentErrors.phone,
    ].filter(Boolean) as string[];
    setAllErrors(newAllErrors);
  }, [email, name, phone, selectedCountryCode, documentType, documentValue]);

  const contextValue = useMemo(
    () => ({
      email,
      setEmail,
      phone,
      setPhone,
      name,
      setName,
      documentArray,
      documentTypes,
      setDocumentTypes,
      documentType,
      setDocumentType,
      selectedCountryCode,
      setSelectedCountryCode,
      errors,
      allErrors,
      isFormValid,
      setErrors,
      setAllErrors,
      setIsFormValid,
      emailRef,
      nameRef,
      phoneRef,
      onSelectDocument,
      validateFields,
      setDocumentValue,
      documentValue,
      documentErrors,
      setDocumentErrors,
    }),
    [
      email,
      setEmail,
      phone,
      setPhone,
      name,
      setName,
      documentArray,
      documentTypes,
      setDocumentTypes,
      documentType,
      setDocumentType,
      selectedCountryCode,
      setSelectedCountryCode,
      errors,
      allErrors,
      isFormValid,
      setErrors,
      setAllErrors,
      setIsFormValid,
      emailRef,
      nameRef,
      phoneRef,
      onSelectDocument,
      validateFields,
      documentValue,
      setDocumentValue,
      documentErrors,
      setDocumentErrors,
    ]
  );

  return (
    <FormContext.Provider value={contextValue}>{children}</FormContext.Provider>
  );
};

export const useFormContext = () => {
  const context = useContext(FormContext);
  if (context === undefined) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
};
